import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, MessageSquare, Send, Swords } from 'lucide-react';

import { useQlashUser } from '@/hooks/useQlashUser';
import { useGetMyLinkQuery } from '@/services/qduelsApi';
import { isNotLinkedError } from '@/services/qduelsErrors';
import { useMeQuery as useQbotMeQuery } from '@/services/qbotApi';
import { useTelegram } from '@/hooks/useTelegram';
import { useLinkedIdentities } from '@/hooks/useLinkedIdentities';
import { cn } from '@/lib/cn';

/**
 * Social identities shown in the "Personal accounts" group. Logto-managed
 * (discord/google/apple/twitch) come from userinfo; Telegram from initData +
 * QBot linkedAccounts. Google/Apple show the associated email (preferEmail).
 */
const SOCIAL_PROVIDERS: ReadonlyArray<{
  target: string;
  label: string;
  className: string;
  icon?: React.ReactNode;
  monogram?: string;
  preferEmail?: boolean;
}> = [
  { target: 'telegram', label: 'Telegram', className: 'bg-sky-500/20 text-sky-400', icon: <Send className="h-4 w-4" /> },
  { target: 'discord', label: 'Discord', className: 'bg-[#5865F2]/20 text-[#5865F2]', icon: <MessageSquare className="h-4 w-4" /> },
  { target: 'google', label: 'Google', className: 'bg-[#EA4335]/20 text-[#EA4335]', monogram: 'G', preferEmail: true },
  { target: 'apple', label: 'Apple', className: 'bg-fg/10 text-fg', monogram: 'A', preferEmail: true },
  { target: 'twitch', label: 'Twitch', className: 'bg-[#9146FF]/20 text-[#9146FF]', monogram: 'Tw' },
];

/**
 * Accounts page — reached from the Profilo "Settings" block. Two separate
 * groups on one page: linked GAME accounts (Clash Royale via qduels) and
 * PERSONAL accounts (the login email first, then Telegram/Discord/Google/
 * Apple/Twitch with live connection state). Link/unlink actions are a
 * follow-up (docs/superpowers/specs/2026-07-24-account-linking-design.md).
 */
export function LinkedAccounts() {
  const { t } = useTranslation('general');
  // Una fonte sola per build: su una build Limulo Laravel non viene interrogato affatto.
  // Il perche' (e la corsa che c'era prima) in `hooks/useQlashUser.ts`.
  const me = useQlashUser();

  const identities = useLinkedIdentities();
  const tg = useTelegram();
  const qbotMe = useQbotMeQuery();
  const qbotLinked = new Map<string, string>(
    (qbotMe.data?.linkedAccounts ?? []).map((a) => [a.platform, a.platformUserId]),
  );

  const crLinkQuery = useGetMyLinkQuery();
  const crNotLinked = isNotLinkedError(crLinkQuery.error);
  const crLinkedTag = crLinkQuery.data?.playerTag ?? null;

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">
          {t('social.accounts_title', { defaultValue: 'Accounts' })}
        </h1>
      </header>

      {/* Group 1 — Linked game accounts */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('social.linked_games', { defaultValue: 'Linked game accounts' })}
        </h2>
        <Link
          to="/duels/link"
          className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 text-brand">
            <Swords className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">
              {t('social.link_clash_royale', { defaultValue: 'Clash Royale' })}
            </p>
            <p className="truncate text-[11px] text-fg-muted">
              {crLinkedTag
                ? t('social.link_cr_linked', { defaultValue: 'Linked · {{tag}}', tag: crLinkedTag })
                : crNotLinked || !crLinkQuery.isLoading
                  ? t('social.link_cr_connect', { defaultValue: 'Link to send duels' })
                  : t('social.coming_soon', { defaultValue: 'Coming soon' })}
            </p>
          </span>
          <ChevronRight className="h-4 w-4 text-fg-muted" />
        </Link>
      </section>

      {/* Group 2 — Personal accounts: login email first, then social identities */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('social.personal_accounts', { defaultValue: 'Personal accounts' })}
        </h2>

        {/* Login email — a non-social account, always shown first. */}
        {me?.email && (
          <div className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-fg/10 text-fg">
              <Mail className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">{t('email', { defaultValue: 'Email' })}</p>
              <p className="truncate text-[11px] text-fg-muted">{me.email}</p>
            </span>
          </div>
        )}

        {SOCIAL_PROVIDERS.map((p) => {
          const isTelegram = p.target === 'telegram';
          const identity = identities?.[p.target];
          const qbotId = qbotLinked.get(p.target);
          const loading = !isTelegram && !qbotId && identities === null;
          const username = isTelegram
            ? tg.user?.username
              ? `@${tg.user.username}`
              : null
            : p.preferEmail
              ? (identity?.email ?? identity?.username ?? null)
              : (identity?.username ?? identity?.email ?? null);
          const isLinked = isTelegram
            ? !!tg.user || qbotLinked.has('telegram')
            : !!identity || !!qbotId;
          return (
            <div
              key={p.target}
              className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3"
            >
              <span
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold',
                  p.className,
                )}
              >
                {p.icon ?? p.monogram}
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-fg">{p.label}</p>
                <p className="truncate text-[11px] text-fg-muted">
                  {loading
                    ? '…'
                    : isLinked
                      ? username
                        ? t('social.connected_as', { defaultValue: 'Connected · {{name}}', name: username })
                        : t('social.connected', { defaultValue: 'Connected' })
                      : t('social.not_connected', { defaultValue: 'Not connected' })}
                </p>
              </span>
              {isLinked && (
                <span className="text-xs font-medium text-brand">
                  {t('social.connected', { defaultValue: 'Connected' })}
                </span>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
